// Engine Range Game — shared contracts. This file is the single source of truth for the shapes that the
// physics, simulation, data, audio and UI modules exchange. Keep it free of runtime logic (constants only).
// Design: docs/superpowers/specs/2026-10-14-engine-range-game-design.md

export type Domain = 'air' | 'sea';

/** A published source for a number or a claim shown in the UI. */
export interface SourceRef { label: string; url: string }

export type EngineKind = 'piston' | 'turboprop' | 'turbofan' | 'diesel' | 'outboard' | 'gas-turbine' | 'two-stroke-diesel';
/** 'verified' = a field recording of this engine type with a commercial-use licence is shipped. */
export type AudioStatus = 'verified' | 'pending';

export interface EngineSpec {
  id: string;
  /** Real, descriptive name as printed on the spec card, e.g. 'Lycoming IO-360-L2A'. */
  name: string;
  kind: EngineKind;
  /** Shaft / brake power at rated rpm (piston, turboprop, marine). */
  ratedPowerKw?: number;
  /** Jets: maximum dry thrust, sea-level static. */
  ratedThrustN?: number;
  afterburnerThrustN?: number;
  dryMassKg: number;
  /** Shaft engines: brake specific fuel consumption at rated / best-power mixture, kg per kWh. */
  bsfcKgPerKwh?: number;
  /** Jets: CRUISE thrust specific fuel consumption, kg per newton per hour (installed). */
  tsfcKgPerNh?: number;
  tsfcAfterburnerKgPerNh?: number;
  /** Turbocharged / turbonormalized: rated power is held up to this altitude. */
  criticalAltM?: number;
  /** Fuel injection is required (with an engine monitor) for lean-of-peak operation. */
  fuelInjected?: boolean;
  propeller?: 'fixed' | 'constant-speed';
  ratedRpm: number;
  idleRpm: number;
  audio: AudioStatus;
  /** Folder under public/audio/engines/ holding manifest.json and the loops. */
  audioBank?: string;
  /** Shown in the hangar when audio is 'pending' ("Real recording not yet sourced: …"). */
  audioNote?: string;
  /** File stem under public/img/engines/ (WebP). */
  photo?: string;
  sources: SourceRef[];
}

export interface AirframeSpec {
  emptyMassKg: number;
  payloadKg: number;
  fuelCapacityKg: number;
  wingAreaM2: number;
  aspectRatio: number;
  /** Oswald efficiency factor. */
  oswald: number;
  /** Zero-lift drag coefficient, clean. */
  cd0: number;
  /** Design cruise true airspeed for a fixed-pitch propeller (m/s). */
  vDesignMs: number;
  /** Clean stall speed at maximum mass (m/s). */
  vStallMs: number;
  ceilingM: number;
  engineCount: number;
}

export type HullType = 'displacement-small' | 'tanker' | 'feeder' | 'container-large' | 'planing';

export interface HullSpec {
  hullType: HullType;
  lwlM: number;
  designSpeedKn: number;
  /** Brake power demanded from the engine(s) at design speed, calm water, clean hull (kW). */
  designPowerKw: number;
  /** Auxiliary ("hotel") load that burns fuel regardless of speed (kW). */
  hotelKw: number;
  fuelCapacityKg: number;
  minSpeedKn: number;
  maxSpeedKn: number;
  engineCount: number;
}

export type Mixture = 'rich' | 'peak' | 'lop';

/** Cruise plan chosen in the briefing; also the live controls during a run. */
export interface CruiseControls {
  /** Aircraft: target cruise altitude (ft). */
  altitudeFt?: number;
  /** Shaft engines: fraction of rated power, one of 0.55 | 0.65 | 0.75. */
  power?: number;
  /** Jets: fraction of available thrust, 0.6 … 1.0. */
  thrust?: number;
  afterburner?: boolean;
  mixture?: Mixture;
  /** Constant-speed propellers only (rpm). */
  propRpm?: number;
  /** Vessels: ordered speed through the water (kn). */
  speedKn?: number;
}

export interface Controls extends CruiseControls {
  /** Weather-cell choice: true = divert around it, false/undefined = push through. */
  divert?: boolean;
}

export interface CraftSpec {
  id: string;
  domain: Domain;
  /** Generic class shown as the title, e.g. 'Four-seat trainer'. */
  className: string;
  /** The real type the numbers come from, e.g. 'Cessna 172S'. */
  modelledOn: string;
  engineId: string;
  airframe?: AirframeSpec;
  hull?: HullSpec;
  costCredits: number;
  /** File stem under public/img/craft/ (WebP). */
  photo: string;
  defaultCruise: CruiseControls;
  sources: SourceRef[];
}

export type ModCategory = 'engine' | 'propulsor' | 'fuel-weight' | 'hull-airframe' | 'operations';

/** Every field is optional; multiplicative fields fold by multiplication, additive by addition. */
export interface ModEffect {
  bsfcMul?: number;
  tsfcMul?: number;
  powerMul?: number;
  thrustMul?: number;
  propEffMul?: number;
  /** Converts a fixed-pitch propeller to constant-speed. */
  propeller?: 'constant-speed';
  /** Sets (or raises) the turbo critical altitude. */
  criticalAltM?: number;
  /** Lets the player select lean-of-peak (needs a fuel-injected engine). */
  enablesLop?: boolean;
  cd0Add?: number;
  inducedMul?: number;
  emptyMassAddKg?: number;
  fuelCapacityAddKg?: number;
  /** External tanks: the added drag disappears once their fuel is used. */
  jettisonWhenEmpty?: boolean;
  foulingReset?: boolean;
  foulingGrowthMul?: number;
  hotelMul?: number;
  /** Propeller / hull measures that cut the power demanded for a given speed. */
  propulsivePowerMul?: number;
}

export interface ModSpec {
  id: string;
  name: string;
  category: ModCategory;
  domain: Domain;
  /** Craft ids this mod can be installed on, or ['*'] for every craft of the domain. */
  appliesTo: string[];
  requires?: string[];
  costCredits: number;
  effect: ModEffect;
  /** One honest line with the documented magnitude, e.g. '−6 % fuel per hp·h (vendor claims 10–15 %)'. */
  claim: string;
  sources: SourceRef[];
}

export interface Build { craftId: string; modIds: string[] }

// ---------------------------------------------------------------- environment

export const AIR_LAYERS_FT = [0, 10000, 20000, 30000, 40000] as const;

export interface WeatherCell {
  /** Extra distance if the player diverts around the cell (nm). */
  divertNm: number;
  /** Extra drag (air) or resistance (sea) fraction if the player pushes through, e.g. 0.15. */
  pushPenalty: number;
}

export interface AirSegment {
  fromNm: number;
  toNm: number;
  /** Along-track wind per AIR_LAYERS_FT layer; positive = tailwind (kt). */
  windKtByLayer: number[];
  weatherCell?: WeatherCell;
}

export interface SeaSegment {
  fromNm: number;
  toNm: number;
  /** Along-track current; positive = favourable (kn). */
  currentKn: number;
  /** Douglas sea state 0–6. */
  seaState: number;
  weatherCell?: WeatherCell;
}

export interface Route {
  seed: number;
  domain: Domain;
  segments: AirSegment[] | SeaSegment[];
  lengthNm: number;
}

/** What the aircraft tick needs from the world. */
export interface AirEnv { windAlongKt: (altFt: number) => number; dragPenalty: number }
/** What the vessel tick needs from the world. */
export interface SeaEnv { currentKn: number; seaState: number; resistancePenalty: number }

// ---------------------------------------------------------------- simulation

export type Phase = 'climb' | 'cruise' | 'ended';

export interface SimState {
  timeS: number;
  distanceNm: number;
  fuelKg: number;
  massKg: number;
  altitudeFt: number;
  /** True airspeed (air) or speed through the water (sea), kt. */
  tasKt: number;
  groundKt: number;
  /** Along-track wind or current component actually applied this tick (kt). */
  windOrCurrentKt: number;
  rpm: number;
  /** Engine load 0..1 (fraction of rated power / available thrust). */
  load: number;
  fuelFlowKgPerH: number;
  specificRangeNmPerKg: number;
  /** Hull fouling as a fraction of added power, 0 = clean. */
  fouling: number;
  seaState: number;
  phase: Phase;
  segmentIndex: number;
  storesJettisoned: boolean;
}

export type DecisionKind = 'top-of-climb' | 'step-climb' | 'conditions-change' | 'weather-cell' | 'reserve';

export interface DecisionOption {
  id: string;
  label: string;
  /** The honest consequence in real units, e.g. 'Climb to 34,000 ft: +3 % specific range, 12 min, 180 kg'. */
  consequence: string;
  controls?: Partial<Controls>;
}

export interface Decision { kind: DecisionKind; title: string; detail: string; options: DecisionOption[] }

export interface RunEvent { timeS: number; distanceNm: number; text: string }

export type EndReason = 'dry-tank' | 'player';

export interface RunResult {
  craftId: string;
  modIds: string[];
  seed: number;
  distanceNm: number;
  fuelUsedKg: number;
  simHours: number;
  /** nm per kg of fuel (air) or nm per tonne (sea). */
  efficiency: number;
  /** efficiency / stock baseline efficiency; 1.00 = stock build at default cruise in still air. */
  efficiencyIndex: number;
  credits: number;
  endedBy: EndReason;
  events: RunEvent[];
  isBest: boolean;
  endedAt: string;
}

export interface Baseline { rangeNm: number; efficiency: number }

export const WARP_LEVELS = [1, 10, 60, 600, 3600] as const;
export type Warp = (typeof WARP_LEVELS)[number];

// ---------------------------------------------------------------- audio

export interface LoopDef { file: string; rpm: number; load: 'on' | 'off' }

export interface BankManifest {
  bank: string;
  engine: string;
  loops: LoopDef[];
  start?: string;
  stop?: string;
  credits: AssetCredit[];
}

export interface AssetCredit {
  file: string;
  title: string;
  author: string;
  url: string;
  licence: string;
  licenceUrl: string;
}

// ---------------------------------------------------------------- save

export interface SaveSettings {
  audio: boolean;
  volume: number;
  reducedMotion: 'system' | 'on' | 'off';
  /** Name shown on the global board, 1–24 characters; defaults to a random 'Pilot-1234'. */
  nickname: string;
  /** Whether completed runs are submitted to the global board (Supabase). */
  online: boolean;
}

export interface SaveData {
  v: 1;
  credits: number;
  owned: string[];
  /** Installed mod ids per craft id. */
  builds: Record<string, string[]>;
  bests: Record<string, { nm: number; eff: number }>;
  /** Most recent first, at most 20. */
  runs: RunResult[];
  /** Results that could not be submitted to the global board yet (offline); retried later, at most 20. */
  pending: RunResult[];
  settings: SaveSettings;
}

export type ScreenId = 'hangar' | 'workshop' | 'briefing' | 'run' | 'results' | 'credits' | 'settings' | 'leaderboard';

// ---------------------------------------------------------------- global board (Supabase)

export interface BoardRow {
  userId: string;
  nickname: string;
  craftId: string;
  distanceNm: number;
  efficiencyIndex: number;
  seed: number;
  createdAt: string;
  modCount: number;
}

export type SubmitOutcome =
  | { ok: true; rank: number | null }
  | { ok: false; reason: 'offline' | 'unconfigured' | 'rejected' | 'error' | 'disabled' };
